import type { KdsSettings, KdsStation, KdsTicket } from '../../types/kds';
import type { TKey } from '../../lib/i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Pure board maths. No React, no DOM, all trivially testable.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Seconds a ticket has been on the board, measured on the SERVER's clock.
 *
 * `skewMs` is (serverNow - clientNow) captured from the last response. Kitchen
 * tablets are routinely minutes out; without this correction a freshly fired
 * ticket renders as long overdue on a slow-clocked device, and as negative on a
 * fast one. Either way the colours stop meaning anything, which is the failure
 * mode that makes a KDS worse than a printer.
 *
 * Clamped at zero: a ticket cannot have been waiting a negative time, and
 * showing "-3s" for the first few seconds after a fire looks like a bug.
 */
export function elapsedSeconds(firedAt: string, skewMs: number, nowMs = Date.now()): number {
  const fired = new Date(firedAt).getTime();
  if (Number.isNaN(fired)) return 0;
  return Math.max(0, Math.floor((nowMs + skewMs - fired) / 1000));
}

/**
 * mm:ss up to an hour, then h:mm. Kitchen timers are read at a glance from two
 * metres away, so no unit suffixes and no leading zero on the minutes.
 */
export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes}:${String(s % 60).padStart(2, '0')}`;
  const hours = Math.floor(minutes / 60);
  return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export type Band = 'fresh' | 'warn' | 'late';

/**
 * Effective thresholds for a ticket: the station's overrides win, the branch
 * settings fill the gaps. A bar measured against the grill's ten-minute
 * critical band would never go red, and a grill measured against the bar's
 * would never be green.
 */
export function bandsFor(
  settings: KdsSettings | null,
  station: KdsStation | undefined,
): { warn: number; critical: number } {
  return {
    warn: station?.warnSeconds ?? settings?.warnSeconds ?? 300,
    critical: station?.criticalSeconds ?? settings?.criticalSeconds ?? 600,
  };
}

export function bandOf(seconds: number, warn: number, critical: number): Band {
  if (seconds >= critical) return 'late';
  if (seconds >= warn) return 'warn';
  return 'fresh';
}

/**
 * Progress against the ticket's own prep target, 0..1+.
 *
 * Returns null when the item has no target — most tenants will never fill in
 * prep times, and a progress bar that is always empty (or always full) is worse
 * than no bar at all, so the card omits it entirely in that case.
 */
export function targetProgress(ticket: KdsTicket, elapsed: number): number | null {
  if (!ticket.targetSeconds || ticket.targetSeconds <= 0) return null;
  return elapsed / ticket.targetSeconds;
}

/**
 * Inv_Receipt_h.OrderType for a delivery order.
 *
 * The one order type the pass treats differently: serving it means handing a
 * bag to somebody who is about to drive off, so the expo screen asks who.
 */
export const ORDER_TYPE_DELIVERY = 2;

/**
 * `Inv_Receipt_h.OrderType` → label, in the legacy POS's own numbering.
 *
 * These five numbers are what the till charges with (`orderTypeCode.ts`) and
 * what the admin's default-order-type setting maps
 * (`orderSettings.repository.ts`). This table used to read
 * `{0: dineIn, 1: walkIn, 2: pickup, 3: delivery, 4: membership}` — a
 * different numbering that matched nothing that writes the column, so every
 * delivery ticket was labelled "Pickup", every dine-in "Delivery", and every
 * pickup "Membership". Checked against GL_WEB_ablaatiaat, where every delivery
 * receipt carries 2.
 *
 * 0 is kept alongside 3 for dine-in: the legacy ERP writes NULL for a table
 * order and some rows carry 0.
 */
const ORDER_TYPE_KEYS: Record<number, TKey> = {
  0: 'dineIn',
  1: 'walkIn',
  2: 'delivery',
  3: 'dineIn',
  4: 'pickup',
  5: 'membership',
};

/**
 * A human label for Inv_Receipt_h.OrderType, in the screen's language.
 *
 * NULL is dine-in in the legacy model — it is not "unknown", it is the value
 * the ERP writes for a table order, and rendering it blank loses the one piece
 * of context that tells the kitchen whether the food is being plated or bagged.
 *
 * A label sent by the server wins over the local dictionary: a tenant who has
 * renamed an order type (several call takeaway something else) has done so in
 * their own words, and second-guessing that in translation would show the cook
 * a name nobody at the branch uses.
 */
export function orderTypeLabel(ticket: KdsTicket, t: (key: TKey) => string): string {
  if (ticket.orderTypeLabel) return ticket.orderTypeLabel;
  if (ticket.orderType == null) return t('dineIn');
  const key = ORDER_TYPE_KEYS[ticket.orderType];
  return key ? t(key) : `#${ticket.orderType}`;
}

const SOURCE_KEYS: Partial<Record<string, TKey>> = {
  menu: 'srcQr',
  online: 'srcOnline',
  offline: 'srcOffline',
  appointment: 'srcBooking',
  call_center: 'srcCallCenter',
};

/**
 * Badge for where the order came from. 'pos' returns null on purpose — the
 * overwhelming majority of tickets are from the till, and a badge on every
 * card is a badge that carries no information.
 */
export function sourceBadge(ticket: KdsTicket, t: (key: TKey) => string): string | null {
  const key = SOURCE_KEYS[ticket.source];
  return key ? t(key) : null;
}

/**
 * Pick the name to lead with.
 *
 * Falls back to the other script rather than showing a blank: plenty of
 * catalogues have Name filled in and Name_AR empty (or the reverse), and an
 * unnamed line on a kitchen ticket is useless in any language.
 */
export function pickName(
  lang: 'en' | 'ar' | string | undefined,
  en: string | null,
  ar: string | null,
): string {
  const arabicFirst = (lang ?? 'ar').startsWith('ar');
  const primary = arabicFirst ? ar : en;
  return (primary || en || ar || '').trim() || '—';
}

/**
 * How far through its lines a ticket is, for the header count and the expo
 * progress dots. Voided lines are excluded — a cancelled item is not work
 * outstanding, and counting it would leave every edited order showing as
 * permanently incomplete.
 */
export function lineProgress(ticket: KdsTicket): { ready: number; total: number } {
  const live = ticket.lines.filter((l) => !l.isVoided);
  return { ready: live.filter((l) => l.status === 'ready').length, total: live.length };
}

/** Tickets a station screen should treat as still needing work. */
export function isOpen(ticket: KdsTicket): boolean {
  return ticket.status === 'queued' || ticket.status === 'preparing' || ticket.status === 'recalled';
}

/**
 * NOTE: `groupModifiers` lived here until 2026-08-31. The card now renders one
 * TAG per chosen option, each carrying its own group name, so there is nothing
 * left to collapse into per-group rows.
 */

const ARABIC_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
const LATIN_RE = /[A-Za-z]/;

/**
 * Split one of the ERP's BILINGUAL attribute titles into its two languages.
 *
 * The legacy per-line options carry both languages in a single column, with no
 * separator and nothing to key off:
 *
 *   "الحجم size"
 *   "ملفوف Malfof"
 *   "حجم صغير ( 3-4 افراد )Smal ( from 3 to 4 persons )"
 *
 * This is NOT a data-modelling choice we can read around.
 * `Inv_ItemCustomAttributeHeader` has no Arabic column at all, and
 * `Inv_ItemCustomAttributeDetail.Title_AR` exists but is an empty string on
 * every row on GL_WEB_ablaatiaat — the tenant typed both languages into
 * `Title`. Verified with `scripts/inspect-legacy-attribute-titles.ts`.
 *
 * So the boundary is found by SCRIPT, which is a heuristic, and it is written
 * to fail safe: anything it cannot split confidently comes back as the whole
 * string in both languages — exactly what the card showed before, never worse.
 *
 * Digits and punctuation are deliberately not treated as a boundary. They
 * appear on both sides ("( 3-4 افراد )" and "( from 3 to 4 persons )"), so the
 * split is taken at the first LETTER of the other script, which keeps each
 * side's own brackets and numbers with it.
 */
export function splitBilingual(title: string): { ar: string; en: string } {
  const s = (title ?? '').trim();
  if (!s) return { ar: '', en: '' };

  let firstAr = -1;
  let lastAr = -1;
  let firstLat = -1;
  let lastLat = -1;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s.charAt(i);
    if (ARABIC_RE.test(ch)) {
      if (firstAr === -1) firstAr = i;
      lastAr = i;
    } else if (LATIN_RE.test(ch)) {
      if (firstLat === -1) firstLat = i;
      lastLat = i;
    }
  }

  // Only one script present — there is nothing to split, and the one string is
  // the best answer in either language.
  if (firstAr === -1 || firstLat === -1) return { ar: s, en: s };

  let ar: string;
  let en: string;
  if (lastAr < firstLat) {
    // Arabic then Latin — every title on the tenant that prompted this.
    ar = s.slice(0, firstLat);
    en = s.slice(firstLat);
  } else if (lastLat < firstAr) {
    // Latin then Arabic. Not seen in the wild, but free to support.
    en = s.slice(0, firstAr);
    ar = s.slice(firstAr);
  } else {
    // The scripts interleave. Splitting would cut a word in half, so don't.
    return { ar: s, en: s };
  }

  ar = ar.trim();
  en = en.trim();
  return { ar: ar || s, en: en || s };
}
